import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import TourButton from '../../TourButton/TourButton';
import { getRecipeModalSteps } from '../../../tours/recipeModal';
import axios from 'axios';
import { useAppContext } from '../../../context/AppContext';
import { useNotification } from '../../notifications/useNotification';
import LoadingOverlay from '../../LoadingOverlay/LoadingOverlay';
import ConfirmationModal from '../ConfirmationModal/ConfirmationModal';
import Box from '@mui/material/Box';
import FormControlLabel from '@mui/material/FormControlLabel';
import Switch from '@mui/material/Switch';
import Tooltip from '@mui/material/Tooltip';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { useMediaQuery } from '@mui/material';
import './RecipeModal.css';
import { sanitizeUrl } from '../../../utils/functions';
import AuthenticatedImage from '../../AuthenticatedImage/AuthenticatedImage';

/* ── Nutrient helpers ────────────────────────────────────────────────────── */

const NUTRIENT_KEYS = [
    { key: 'kcal',         labelKey: 'rm-nut-kcal',     highlight: true  },
    { key: 'fats',         labelKey: 'rm-nut-fats'                       },
    { key: 'satured_fats', labelKey: 'rm-nut-satfats'                    },
    { key: 'carbs',        labelKey: 'rm-nut-carbs'                      },
    { key: 'sugars',       labelKey: 'rm-nut-sugars'                     },
    { key: 'proteins',     labelKey: 'rm-nut-proteins'                   },
    { key: 'fibers',       labelKey: 'rm-nut-fibers'                     },
    { key: 'salt',         labelKey: 'rm-nut-salt'                       },
];

const calcNutrient = (basePerHundred, grams) => {
    if (basePerHundred == null || grams === '' || grams == null) return 0;
    const g = parseFloat(grams);
    if (isNaN(g)) return 0;
    return Math.round((basePerHundred / 100) * g * 10) / 10;
};

const buildFoodListItem = (food, grams, currentLanguage, fallbackId = null) => ({
    foodId: food?._id ?? fallbackId,
    grams,
    displayName: food ? (food.name?.[currentLanguage] ?? food.name?.en ?? '') : '',
    baseNutrients: food ? {
        kcal:         food.kcal         ?? 0,
        fats:         food.fats         ?? 0,
        satured_fats: food.satured_fats ?? 0,
        carbs:        food.carbs        ?? 0,
        sugars:       food.sugars       ?? 0,
        proteins:     food.proteins     ?? 0,
        fibers:       food.fibers       ?? 0,
        salt:         food.salt         ?? 0,
    } : null,
});

/* ── Language constants ─────────────────────────────────────────────────── */

const SUPPORTED_LANGUAGES = ['en', 'it'];

const LANGUAGE_LABELS = {
    en: 'English',
    it: 'Italiano',
};

/* ── Component ───────────────────────────────────────────────────────────── */

/**
 * RecipeModal — Add or Edit a recipe.
 *
 * Props:
 *   setShowModal  {Function}  — closes the modal
 *   onSave        {Function}  — called after a successful save
 *   recipe        {Object?}   — if provided, modal runs in edit mode
 */
const RecipeModal = ({ setShowModal, onSave, recipe }) => {
    const { t } = useTranslation();
    const { dietist, currentLanguage } = useAppContext();
    const { showSuccess, showError } = useNotification();
    const { appColors } = useAppContext();
    const isMobile = useMediaQuery('(max-width: 768px)');
    const fileInputRef = useRef(null);
    const foodSearchRef = useRef(null);
    const langPickerRef = useRef(null);

    const [loading, setLoading]   = useState(false);
    const [errors, setErrors]     = useState({});

    const [formData, setFormData] = useState({
        servings:        recipe?.servings          ?? '',
        preparationTime: recipe?.preparationTime   ?? '',
    });

    // Language-keyed name & description data
    const [langData, setLangData] = useState(() => {
        const data = {};
        data[currentLanguage] = {
            name:        recipe?.name?.[currentLanguage]        ?? '',
            description: recipe?.description?.[currentLanguage] ?? '',
        };
        if (recipe) {
            for (const lang of SUPPORTED_LANGUAGES) {
                if (lang !== currentLanguage && (recipe?.name?.[lang] || recipe?.description?.[lang])) {
                    data[lang] = {
                        name:        recipe?.name?.[lang]        ?? '',
                        description: recipe?.description?.[lang] ?? '',
                    };
                }
            }
        }
        return data;
    });
    const [showLangPicker, setShowLangPicker] = useState(false);

    // Food list: [{ foodId, grams, displayName, baseNutrients }]  — displayName/baseNutrients are UI-only
    const [foodList, setFoodList] = useState([]);

    // Image
    const [image, setImage]               = useState(null);
    const [imagePreview, setImagePreview] = useState(sanitizeUrl(recipe?.imageUrl ?? recipe?.image) ?? null);

    // Food search
    const [foodSearchList, setFoodSearchList]     = useState([]);
    const [foodSearchQuery, setFoodSearchQuery]   = useState('');
    const [showFoodDropdown, setShowFoodDropdown] = useState(false);
    const [selectedFood, setSelectedFood]         = useState(null);
    const [selectedGrams, setSelectedGrams]       = useState('');
    const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
    const [onlyGenerics, setOnlyGenerics]         = useState(true); // filter food search to generic brand only

    /* ── Live food search (same endpoint as AddMealModal) ───────────── */
    const searchFoodsByName = async (name) => {
        if (!name || name.trim() === '') { setFoodSearchList([]); return; }
        try {
            let url = `${process.env.REACT_APP_BACKEND_URL}/foods/?nameFilter=${encodeURIComponent(name)}&language=${currentLanguage}`;
            if (onlyGenerics) url += '&onlyGenerics=true';
            const res = await axios.get(url, { withCredentials: true });
            setFoodSearchList(res.data ?? []);
        } catch (err) {
            console.error('RecipeModal: food search failed', err);
        }
    };

    /* ── Close dropdown on outside click ─────────────────────────────── */
    useEffect(() => {
        const handleClickOutside = (e) => {
            if (foodSearchRef.current && !foodSearchRef.current.contains(e.target)) {
                setShowFoodDropdown(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    /* ── Close lang picker on outside click ─────────────────────────── */
    useEffect(() => {
        const handleClickOutside = (e) => {
            if (langPickerRef.current && !langPickerRef.current.contains(e.target)) {
                setShowLangPicker(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    /* ── Restore ingredients in edit mode ─────────────────────────────── */
    useEffect(() => {
        const hydrateRecipeFoods = async () => {
            const recipeFoods = recipe?.foodsList ?? recipe?.foodList ?? [];

            if (!recipeFoods.length) {
                setFoodList([]);
                return;
            }

            try {
                setLoading(true);

                const restoredFoods = await Promise.all(
                    recipeFoods.map(async (item) => {
                        const rawFood = item?.foodId;
                        const grams = item?.grams ?? '';

                        if (rawFood && typeof rawFood === 'object') {
                            return buildFoodListItem(rawFood, grams, currentLanguage, rawFood._id);
                        }

                        const foodId = rawFood ?? item?.id ?? item?._id ?? item?.food;
                        if (!foodId) return null;

                        const res = await axios.get(
                            `${process.env.REACT_APP_BACKEND_URL}/foods/${foodId}`,
                            { withCredentials: true }
                        );

                        const foodData = res.data?.food ?? res.data?.data ?? res.data;
                        return buildFoodListItem(foodData, grams, currentLanguage, foodId);
                    })
                );

                setFoodList(restoredFoods.filter(Boolean));
            } catch (error) {
                console.error('RecipeModal: failed to restore recipe ingredients', error);
                showError(t('rm-error-load-ingredients'));
                setFoodList([]);
            } finally {
                setLoading(false);
            }
        };

        hydrateRecipeFoods();
    }, [recipe, currentLanguage, showError, t]);



    /* ── Handlers ─────────────────────────────────────────────────────── */

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
        if (errors[name]) setErrors(prev => ({ ...prev, [name]: null }));
    };

    const handleImageChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setImage(file);
            setImagePreview(URL.createObjectURL(file));
        }
    };

    const handleLangDataChange = (lang, field, value) => {
        setLangData(prev => ({
            ...prev,
            [lang]: { ...prev[lang], [field]: value },
        }));
        if (field === 'name' && errors[`name_${lang}`]) {
            setErrors(prev => ({ ...prev, [`name_${lang}`]: null }));
        }
    };

    const handleAddLanguage = (lang) => {
        setLangData(prev => ({ ...prev, [lang]: { name: '', description: '' } }));
        setShowLangPicker(false);
    };

    const handleRemoveLanguage = (lang) => {
        setLangData(prev => {
            const copy = { ...prev };
            delete copy[lang];
            return copy;
        });
    };

    const handleSelectFood = (food) => {
        setSelectedFood(food);
        setFoodSearchQuery(food.name?.[currentLanguage] ?? food.name?.en ?? '');
        setShowFoodDropdown(false);
    };

    const handleAddFoodToList = () => {
        if (!selectedFood || !selectedGrams) return;
        const grams = parseFloat(selectedGrams);
        if (isNaN(grams) || grams <= 0) return;

        const hydratedFood = buildFoodListItem(selectedFood, grams, currentLanguage, selectedFood._id);

        setFoodList(prev => {
            const existing = prev.find(item => item.foodId === selectedFood._id);
            if (existing) {
                return prev.map(item =>
                    item.foodId === selectedFood._id ? hydratedFood : item
                );
            }
            return [...prev, hydratedFood];
        });

        setSelectedFood(null);
        setFoodSearchQuery('');
        setSelectedGrams('');
    };

    const handleRemoveFood = (foodId) => {
        setFoodList(prev => prev.filter(item => item.foodId !== foodId));
    };

    const handleGramsChange = (foodId, value) => {
        setFoodList(prev => prev.map(item =>
            item.foodId === foodId ? { ...item, grams: value } : item
        ));
    };

    const validateForm = () => {
        const newErrors = {};
        Object.entries(langData).forEach(([lang, ld]) => {
            if (!ld.name?.trim()) newErrors[`name_${lang}`] = t('rm-error-name-required');
        });
        if (!formData.servings) newErrors.servings = t('rm-error-servings-required');
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async () => {
        if (!validateForm()) return;

        try {
            setLoading(true);

            // Compute per-recipe nutrient totals from the ingredient list
            const nutrientTotals = NUTRIENT_KEYS.reduce((acc, n) => {
                const total = foodList.reduce((sum, item) =>
                    sum + (item.baseNutrients
                        ? calcNutrient(item.baseNutrients[n.key], item.grams)
                        : 0)
                , 0);
                acc[n.key] = Math.round(total * 10) / 10;
                return acc;
            }, {});

            const data = new FormData();
            Object.entries(langData).forEach(([lang, ld]) => {
                data.append(`name[${lang}]`,        ld.name.trim());
                data.append(`description[${lang}]`, ld.description.trim());
            });
            data.append('servings',        formData.servings);
            data.append('preparationTime', formData.preparationTime);
            data.append('foodsList',       JSON.stringify(
                foodList.map(({ foodId, grams }) => ({ foodId, grams }))
            ));
            // Nutrient totals
            Object.entries(nutrientTotals).forEach(([key, val]) => {
                data.append(key, val);
            });
            if (image) data.append('image', image);

            if (recipe) {
                await axios.put(
                    `${process.env.REACT_APP_BACKEND_URL}/recipes/${recipe._id}/`,
                    data,
                    { withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' } }
                );
            } else {
                await axios.post(
                    `${process.env.REACT_APP_BACKEND_URL}/recipes/`,
                    data,
                    { withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' } }
                );
            }

            await onSave();
            showSuccess(recipe ? t('rm-success-edit') : t('rm-success-add'));
            handleClose();
        } catch (error) {
            showError(recipe ? t('rm-error-edit') : t('rm-error-add'));
            console.error('RecipeModal error:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleDeleteRecipe = async () => {
        if (!recipe?._id) return;

        try {
            setLoading(true);
            await axios.delete(
                `${process.env.REACT_APP_BACKEND_URL}/recipes/${recipe._id}`,
                { withCredentials: true }
            );
            await onSave();
            showSuccess(t('rm-success-delete-recipe'));
            setShowDeleteConfirmation(false);
            handleClose();
        } catch (error) {
            showError(t('rm-error-delete-recipe'));
            console.error('RecipeModal delete error:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleClose = () => setShowModal(false);

    /* ── Render ───────────────────────────────────────────────────────── */

    return (
        <>
            <LoadingOverlay open={loading} />
            {showDeleteConfirmation && (
                <ConfirmationModal
                    id="delete-recipe-confirmation-modal"
                    titleText={t('rm-delete-recipe')}
                    bodyText={t('rm-confirm-delete-recipe')}
                    handleConfirm={handleDeleteRecipe}
                    handleClose={() => setShowDeleteConfirmation(false)}
                />
            )}

            <div className="rm-overlay">
                <div className="rm-modal" data-tour="rm-modal">

                    {/* ── Header ── */}
                    <div className="rm-header">
                        <div className="rm-header-text">
                            <h2 className="rm-title">
                                {recipe ? t('rm-title-edit') : t('rm-title-add')}
                            </h2>
                            <p className="rm-subtitle">
                                {recipe ? t('rm-subtitle-edit') : t('rm-subtitle-add')}
                            </p>
                        </div>
                        <div className='rm-tutorial-btn'>
                            <TourButton steps={getRecipeModalSteps(t)} iconOnly={isMobile} />
                        </div>
                        <button className="rm-close-btn" onClick={handleClose} aria-label="Close">
                            <span className="material-symbols-outlined">close</span>
                        </button>
                    </div>

                    {/* ── Body ── */}
                    <div className="rm-body">

                        {/* Names & Descriptions (per language) */}
                        <div className="rm-section" data-tour="rm-names">
                            <div className="rm-section-header">
                                <span className="rm-section-label">{t('rm-section-names')}</span>
                                {SUPPORTED_LANGUAGES.filter(l => !Object.keys(langData).includes(l)).length > 0 && (
                                    <div className="rm-lang-picker-wrap" ref={langPickerRef}>
                                        <button
                                            type="button"
                                            className="rm-add-lang-btn"
                                            onClick={() => setShowLangPicker(prev => !prev)}
                                            title={t('rm-add-language')}
                                        >
                                            <span className="material-symbols-outlined">add</span>
                                        </button>
                                        {showLangPicker && (
                                            <div className="rm-lang-picker-dropdown">
                                                {SUPPORTED_LANGUAGES.filter(l => !Object.keys(langData).includes(l)).map(lang => (
                                                    <div
                                                        key={lang}
                                                        className="rm-lang-picker-item"
                                                        onMouseDown={() => handleAddLanguage(lang)}
                                                    >
                                                        {LANGUAGE_LABELS[lang]}
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                            {Object.entries(langData).map(([lang, ld]) => (
                                <div key={lang} className="rm-lang-block">
                                    {lang !== currentLanguage && (
                                        <div className="rm-lang-block-header">
                                            <span className="rm-lang-block-label">{LANGUAGE_LABELS[lang]}</span>
                                            <button
                                                type="button"
                                                className="rm-remove-lang-btn"
                                                onClick={() => handleRemoveLanguage(lang)}
                                                title={t('rm-remove-language')}
                                            >
                                                <span className="material-symbols-outlined">close</span>
                                            </button>
                                        </div>
                                    )}
                                    <div className="rm-field">
                                        <label className="rm-label">
                                            {t('rm-name')}
                                            <span className="rm-required">*</span>
                                        </label>
                                        <input
                                            className={`rm-input${errors[`name_${lang}`] ? ' error' : ''}`}
                                            type="text"
                                            value={ld.name}
                                            onChange={e => handleLangDataChange(lang, 'name', e.target.value)}
                                            placeholder={t('rm-name-placeholder')}
                                        />
                                        {errors[`name_${lang}`] && (
                                            <span className="rm-error">{errors[`name_${lang}`]}</span>
                                        )}
                                    </div>
                                    <div className="rm-field">
                                        <label className="rm-label">{t('rm-description')}</label>
                                        <textarea
                                            className="rm-textarea"
                                            value={ld.description}
                                            onChange={e => handleLangDataChange(lang, 'description', e.target.value)}
                                            placeholder={t('rm-description-placeholder')}
                                            rows={4}
                                        />
                                    </div>
                                </div>
                            ))}
                        </div>

                        {/* Details */}
                        <div className="rm-section" data-tour="rm-details">
                            <span className="rm-section-label">{t('rm-section-details')}</span>
                            <div className="rm-row">
                                <div className="rm-field">
                                    <label className="rm-label">
                                        {t('rm-servings')}
                                        <span className="rm-required">*</span>
                                    </label>
                                    <input
                                        className={`rm-input${errors.servings ? ' error' : ''}`}
                                        type="number"
                                        name="servings"
                                        value={formData.servings}
                                        onChange={handleInputChange}
                                        placeholder="1"
                                        min="1"
                                    />
                                    {errors.servings && <span className="rm-error">{errors.servings}</span>}
                                </div>
                                <div className="rm-field">
                                    <label className="rm-label">{t('rm-preparation-time')}</label>
                                    <input
                                        className="rm-input"
                                        type="number"
                                        name="preparationTime"
                                        value={formData.preparationTime}
                                        onChange={handleInputChange}
                                        placeholder={t('rm-preparation-time-placeholder')}
                                        min="0"
                                    />
                                </div>
                            </div>
                        </div>

                        {/* Image */}
                        <div className="rm-section">
                            <span className="rm-section-label">{t('rm-section-image')}</span>
                            <div
                                className="rm-image-upload-area"
                                onClick={() => fileInputRef.current.click()}
                            >
                                {imagePreview ? (
                                    <AuthenticatedImage src={imagePreview} fallbackSrc={null} alt="recipe preview" className="rm-image-preview" />
                                ) : (
                                    <div className="rm-image-placeholder">
                                        <span className="material-symbols-outlined">add_photo_alternate</span>
                                        <p>{t('rm-image-click-to-upload')}</p>
                                    </div>
                                )}
                                <input
                                    type="file"
                                    ref={fileInputRef}
                                    style={{ display: 'none' }}
                                    accept="image/*"
                                    onChange={handleImageChange}
                                />
                            </div>
                        </div>

                        {/* Food list */}
                        <div className="rm-section" data-tour="rm-ingredients">
                            <span className="rm-section-label">{t('rm-section-food-list')}</span>

                            {/* Search + grams + add */}
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                <Tooltip
                                    title={t('only-generic-brand-info')}
                                    arrow
                                    placement='top'
                                >
                                    <InfoOutlinedIcon sx={{ fontSize: '1rem', color: '#94a3b8', mr: 0.5, cursor: 'default' }} />
                                </Tooltip>
                                <FormControlLabel
                                    control={
                                        <Switch
                                            size='small'
                                            checked={onlyGenerics}
                                            onChange={(e) => {
                                                setOnlyGenerics(e.target.checked);
                                                setFoodSearchList([]);
                                                setFoodSearchQuery('');
                                                setSelectedFood(null);
                                            }}
                                            sx={{
                                                '& .MuiSwitch-switchBase.Mui-checked': { color: appColors.secondary },
                                                '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: appColors.secondary },
                                            }}
                                        />
                                    }
                                    label={<span style={{ fontSize: '0.85rem' }}>{t('only-generic-brand')}</span>}
                                    sx={{ m: 0 }}
                                />
                            </Box>
                            <div className="rm-food-add-row">
                                <div className="rm-food-search-wrap" ref={foodSearchRef}>
                                    <input
                                        className="rm-input"
                                        type="text"
                                        value={foodSearchQuery}
                                        onChange={e => {
                                            const q = e.target.value;
                                            setFoodSearchQuery(q);
                                            setSelectedFood(null);
                                            setShowFoodDropdown(true);
                                            searchFoodsByName(q);
                                        }}
                                        onFocus={() => { if (foodSearchQuery) setShowFoodDropdown(true); }}
                                        placeholder={t('rm-food-search-placeholder')}
                                    />
                                    {showFoodDropdown && foodSearchList.length > 0 && (
                                        <div className="rm-food-dropdown">
                                            {foodSearchList.map(f => (
                                                <div
                                                    key={f._id}
                                                    className="rm-food-dropdown-item"
                                                    onMouseDown={() => handleSelectFood(f)}
                                                >
                                                    {f.name?.[currentLanguage] ?? f.name?.en ?? ''}
                                                    {f.brand && (
                                                        <span className="rm-food-dropdown-brand">
                                                            {typeof f.brand === 'object'
                                                                ? (f.brand?.[currentLanguage] ?? f.brand?.en ?? '')
                                                                : f.brand}
                                                        </span>
                                                    )}
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                                <input
                                    className="rm-input rm-grams-input"
                                    type="number"
                                    value={selectedGrams}
                                    onChange={e => setSelectedGrams(e.target.value)}
                                    placeholder={t('rm-grams-placeholder')}
                                    min="0"
                                />
                                <button
                                    className="rm-add-food-btn"
                                    onClick={handleAddFoodToList}
                                    disabled={!selectedFood || !selectedGrams}
                                    title={t('rm-add-food-btn')}
                                >
                                    <span className="material-symbols-outlined">add</span>
                                </button>
                            </div>

                            {/* Added foods */}
                            {foodList.length === 0 ? (
                                <p className="rm-no-foods">{t('rm-no-foods-added')}</p>
                            ) : (
                                <div className="rm-ing-table-wrap">
                                    <table className="rm-ing-table">
                                        <thead>
                                            <tr className="rm-ing-row rm-ing-head">
                                                <th className="rm-ing-cell rm-ing-name-col">{t('rm-col-ingredient')}</th>
                                                <th className="rm-ing-cell">{t('rm-col-grams')}</th>
                                                {NUTRIENT_KEYS.map(n => (
                                                    <th key={n.key} className={`rm-ing-cell${n.highlight ? ' highlight' : ''}`}>
                                                        {t(n.labelKey)}
                                                    </th>
                                                ))}
                                                <th className="rm-ing-cell rm-ing-del-col"></th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {/* Totals row */}
                                            <tr className="rm-ing-row rm-ing-totals">
                                                <td className="rm-ing-cell rm-ing-name-col">{t('rm-col-total')}</td>
                                                <td className="rm-ing-cell">—</td>
                                                {NUTRIENT_KEYS.map(n => {
                                                    const total = foodList.reduce((sum, item) =>
                                                        sum + (item.baseNutrients
                                                            ? calcNutrient(item.baseNutrients[n.key], item.grams)
                                                            : 0)
                                                    , 0);
                                                    return (
                                                        <td key={n.key} className={`rm-ing-cell${n.highlight ? ' highlight' : ''}`}>
                                                            {Math.round(total * 10) / 10}
                                                        </td>
                                                    );
                                                })}
                                                <td className="rm-ing-cell rm-ing-del-col"></td>
                                            </tr>
                                            {/* Food rows */}
                                            {foodList.map(item => (
                                                <tr key={item.foodId} className="rm-ing-row">
                                                    <td className="rm-ing-cell rm-ing-name-col">
                                                        <span className="rm-ing-food-name" title={item.displayName}>
                                                            {item.displayName}
                                                        </span>
                                                    </td>
                                                    <td className="rm-ing-cell">
                                                        <input
                                                            className="rm-grams-edit"
                                                            type="number"
                                                            value={item.grams}
                                                            onChange={e => handleGramsChange(item.foodId, e.target.value)}
                                                            min="0"
                                                        />
                                                    </td>
                                                    {NUTRIENT_KEYS.map(n => (
                                                        <td key={n.key} className={`rm-ing-cell${n.highlight ? ' highlight' : ''}`}>
                                                            {item.baseNutrients
                                                                ? calcNutrient(item.baseNutrients[n.key], item.grams)
                                                                : '—'}
                                                        </td>
                                                    ))}
                                                    <td className="rm-ing-cell rm-ing-del-col">
                                                        <button
                                                            className="rm-remove-btn"
                                                            onClick={() => handleRemoveFood(item.foodId)}
                                                            title={t('rm-remove-food')}
                                                        >
                                                            <span className="material-symbols-outlined">close</span>
                                                        </button>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>

                    </div>

                    {/* ── Footer ── */}
                    <div className="rm-footer" data-tour="rm-footer">
                        <div className="rm-footer-left">
                            {recipe && (
                                <button className="rm-btn rm-btn-delete" onClick={() => setShowDeleteConfirmation(true)}>
                                    <span className="material-symbols-outlined">delete</span>
                                    {t('rm-delete-recipe')}
                                </button>
                            )}
                        </div>
                        <div className="rm-footer-right">
                            <button className="rm-btn rm-btn-cancel" onClick={handleClose}>
                                {t('Cancel')}
                            </button>
                            <button className="rm-btn rm-btn-save" onClick={handleSubmit}>
                                {t('Save')}
                            </button>
                        </div>
                    </div>

                </div>
            </div>
        </>
    );
};

export default RecipeModal;
